/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Image Source Input Node
   Loads browser-native image files (PNG, JPEG, WebP, AVIF) or procedural
   test cards. Handles large image scaling safely against GPU limits.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import type { TextureHandle } from '../../rendering/types';
import { createImageValue, type ImageValue } from '../../rendering/media';

// High-fidelity studio test pattern preserving clean vector lines and color gradients
function createProceduralTestCard(): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d')!;

  // Obsidian dark studio gradient background
  const grad = ctx.createLinearGradient(0, 0, 1024, 1024);
  grad.addColorStop(0, '#0a0a0c');
  grad.addColorStop(0.5, '#1e1e24');
  grad.addColorStop(1, '#141418');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1024, 1024);

  // Calibration color rings
  const ringColors = ['#b99af8', '#63d9db', '#8fe46c', '#f59e0b', '#ec4899'];
  for (let i = 0; i < 5; i++) {
    ctx.beginPath();
    ctx.arc(512, 512, 380 - i * 65, 0, Math.PI * 2);
    ctx.strokeStyle = ringColors[i];
    ctx.lineWidth = 8;
    ctx.stroke();
  }

  // Crosshairs & alignment grid
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(512, 60);
  ctx.lineTo(512, 964);
  ctx.moveTo(60, 512);
  ctx.lineTo(964, 512);
  ctx.stroke();

  // Corner registration marks
  const corners = [
    [100, 100],
    [924, 100],
    [100, 924],
    [924, 924],
  ];
  for (const [cx, cy] of corners) {
    ctx.strokeRect(cx - 20, cy - 20, 40, 40);
  }

  // Typography
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 44px Instrument Sans, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('ALTERLY STUDIO', 512, 480);
  ctx.font = '22px Instrument Sans, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.fillText('1024 × 1024 • sRGB GPU Master Source', 512, 540);

  return canvas;
}

export const imageInputNode: NodeDefinition = {
  type: 'input.image',
  category: 'Input',
  label: 'Image Source',
  description: 'Loads a browser-local image file (PNG, JPG, WebP) or test pattern as a GPU texture',
  keywords: ['image', 'load', 'import', 'photo', 'texture', 'upload', 'source'],
  targetBackend: 'webgl2',
  inputs: [],
  outputs: [
    { id: 'image', name: 'Image', type: 'image' },
  ],
  params: [
    {
      id: 'sourceType',
      name: 'Source',
      type: 'select',
      defaultValue: 'testCard',
      options: [
        { label: 'Studio Test Pattern', value: 'testCard' },
        { label: 'Local File Upload', value: 'file' },
      ],
    },
    { id: 'imageDataUrl', name: 'File Data', type: 'text', defaultValue: '' },
  ],
  processor: async (_inputs, params, context) => {
    if (params.__media) {
      return {
        image: params.__media,
      };
    }

    if (typeof document === 'undefined') {
      const texture: TextureHandle = {
        id: 'mock_tex_headless',
        width: 1024,
        height: 1024,
        nativeHandle: null,
        dispose: () => {},
      };
      return {
        image: createImageValue(texture, { width: 1024, height: 1024 }),
      };
    }

    let sourceElement: HTMLCanvasElement | HTMLImageElement = createProceduralTestCard();
    let originalWidth = 1024;
    let originalHeight = 1024;

    const dataUrl = (params.imageDataUrl as string) || '';
    if (params.sourceType === 'file' && dataUrl.startsWith('data:image/')) {
      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to decode local image file.'));
      });
      sourceElement = img;
      originalWidth = img.naturalWidth || img.width;
      originalHeight = img.naturalHeight || img.height;
    }

    if (context.signal.aborted) {
      throw new DOMException('Execution aborted', 'AbortError');
    }

    // Large image GPU capability check:
    const maxTexSize = context.capabilities.maxTextureSize || 4096;
    let uploadSource: HTMLCanvasElement | HTMLImageElement = sourceElement;
    let uploadWidth = originalWidth;
    let uploadHeight = originalHeight;

    if (originalWidth > maxTexSize || originalHeight > maxTexSize) {
      context.warn(`Image dimensions (${originalWidth}×${originalHeight}) exceed GPU maximum texture size (${maxTexSize}). Scaling for GPU processing.`);
      const scale = Math.min(maxTexSize / originalWidth, maxTexSize / originalHeight);
      uploadWidth = Math.max(1, Math.round(originalWidth * scale));
      uploadHeight = Math.max(1, Math.round(originalHeight * scale));

      const scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = uploadWidth;
      scaledCanvas.height = uploadHeight;
      const ctx = scaledCanvas.getContext('2d')!;
      ctx.drawImage(sourceElement, 0, 0, uploadWidth, uploadHeight);
      uploadSource = scaledCanvas;
    }

    let texture: TextureHandle;
    try {
      texture = context.backend.createTexture(uploadSource, uploadWidth, uploadHeight);
    } catch {
      // Fallback for headless test environments
      texture = {
        id: 'mock_tex',
        width: uploadWidth,
        height: uploadHeight,
        nativeHandle: null,
        dispose: () => {},
      };
    }

    context.allocateResource(texture);

    const imageValue: ImageValue = createImageValue(texture, {
      width: uploadWidth,
      height: uploadHeight,
      sourceType: params.sourceType === 'file' ? 'upload' : 'generated',
      sourceDimensions: { width: originalWidth, height: originalHeight },
      originalSource: sourceElement,
    });

    return {
      image: imageValue,
    };
  },
};
